/** @jsx jsx */

import { MessageBar } from '@zapier/common-components';
import { jsx } from '@emotion/core';
import { Border, Colors } from '@zapier/theme';
import { isTierFree } from '@zapier/design-system-revenue/src/utils';
import { PlanV2 } from '@zapier/design-system-revenue/src/types';

import { AmountDue } from './AmountDue';
import { AmountDueOnNextInterval } from './AmountDueOnNextInterval';
import { InvoiceLine } from './InvoiceLine';
import { ChangePlanSummarySkeleton } from './ChangePlanSummarySkeleton';
import { useInvoicePreview } from './useInvoicePreview';

type Props = {
  accountId: number;
  plan: PlanV2;
};

const breakdownCss = {
  borderBottom: `${Border.size.default} solid ${Border.color.default}`,
  padding: 10,
};

const sectionCss = {
  backgroundColor: Colors.white,
  border: `${Border.size.default} solid ${Border.color.default}`,
  borderRadius: Border.radius.default,
  lineHeight: '1.6em',
  overflow: 'hidden',
};

export const ChangePlanSummary = ({ accountId, plan }: Props) => {
  const { error, invoice, isLoading } = useInvoicePreview({
    accountId,
    planId: plan.id,
  });

  if (isLoading || !invoice) {
    return <ChangePlanSummarySkeleton />;
  }

  if (error || !plan) {
    return <MessageBar type="error">{error}</MessageBar>;
  }

  const tiers = plan.tiers;
  const freeTier = tiers.find(isTierFree);
  const planNumFreeMeteredTasks = freeTier ? freeTier.numTasks : 0;

  return (
    <section css={sectionCss}>
      {invoice.items.length > 0 && (
        <div css={breakdownCss}>
          {invoice.items.map((line: any) => (
            <InvoiceLine key={line.id} line={line} />
          ))}
        </div>
      )}
      <AmountDue amountDue={invoice.amountDue} />
      {plan.amount > 0 && (
        <AmountDueOnNextInterval
          isPlanMetered={tiers.length > 0}
          meteredTiers={tiers}
          nextPaymentDate={invoice.periodEnd}
          planBillingAmount={plan.amount}
          planInterval={plan.interval}
          planNumFreeMeteredTasks={planNumFreeMeteredTasks}
        />
      )}
    </section>
  );
};
